import { Injectable } from '@angular/core';
import { AuthenticationFacade } from './authentication.facade';
import { SessionContextService } from './session-context.service';

/**
 * Roles que otorgan acceso al módulo de administración de la organización.
 *
 * IMPORTANTE: un miembro de organización que NO es declarado administrador recibe el rol
 * `management_org_viewer` (lector). Ese rol NO debe considerarse administrador, por eso aquí
 * se enumeran únicamente los roles de administración reales en vez de usar el prefijo
 * `management_` (que incluiría al lector). Los roles `platform_*` corresponden a staff de
 * plataforma y se evalúan aparte.
 */
export const ADMIN_ROLES: readonly string[] = [
  'management_org_admin',
  'management_super_admin',
  'superadmin',
];

@Injectable({
  providedIn: 'root'
})
export class AuthorizationService {
  constructor(
    private auth: AuthenticationFacade,
    private sessionContext: SessionContextService
  ) {}
  
  getUserRoles(): string[] {
    const user = this.auth.getCurrentUser();
    return user?.roles || [];
  }

  getPlatformRoles(): string[] {
    return this.sessionContext.getPlatformRoles();
  }
  
  hasRole(role: string): boolean {
    return this.getUserRoles().includes(role);
  }
  
  hasAnyRole(roles: string[]): boolean {
    const userRoles = this.getUserRoles();
    return roles.some(role => userRoles.includes(role));
  }
  
  hasAllRoles(roles: string[]): boolean {
    const userRoles = this.getUserRoles();
    return roles.every(role => userRoles.includes(role));
  }
  
  hasPermission(permission: string): boolean {
    const roles = this.getUserRoles();
    return roles.length > 0;
  }

  /**
   * Determina si el usuario es administrador (de organización o de plataforma) y por tanto
   * puede ver/acceder al módulo de administración.
   *
   * Solo cuentan los roles de administración reales ([[ADMIN_ROLES]]) y los roles de
   * plataforma (`platform_*`). Un `management_org_viewer` (miembro no administrador) NO es
   * admin, aunque tenga el prefijo `management_`.
   */
  isAdmin(): boolean {
    const isAdminRole = (role: string): boolean =>
      ADMIN_ROLES.includes(role) || role.startsWith('platform_');

    // Roles de la organización (membresía del usuario) y roles de plataforma (incluye
    // sesiones delegadas de staff).
    return this.getUserRoles().some(isAdminRole) || this.getPlatformRoles().some(isAdminRole);
  }

  hasServiceAccess(service: string): boolean {
    const roles = this.getUserRoles();
    return roles.some(role => 
      role.startsWith(`${service}_`) || 
      role.startsWith('management_')
    );
  }
}

